import { createFileRoute,Outlet,useRouterState } from '@tanstack/react-router'
import { useCallback,useEffect,useMemo,useRef,useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AppSidebar } from '@/components/dashboard/sidebar'
import { Header } from '@/components/dashboard/header'
import { RouteError } from '@/components/route-error'
import { RoutePending } from '@/components/route-pending'
import { SidebarInset,SidebarProvider } from '@/components/ui/sidebar'
import { api } from '@/lib/api-client'
import { queryKeys } from '@/lib/query-keys'
import { StoreSelectionModal } from '@/components/dashboard/store-selection-modal'
import { authManager,requireAuth } from '@/lib/auth'
import { getSelectedStore,setSelectedStore,subscribeSelectedStore } from '@/features/shared'
import { PosSessionProvider } from '@/features/pos/session/pos-session-context'
import { PosHeaderLeft,PosHeaderRight,PosSearchInput } from '@/features/pos/v2/components/pos-nav-bar'
import { PosTerminalUIProvider } from '@/features/pos/v2/components/pos-terminal'
import { ResumeShiftFab } from '@/components/dashboard/resume-shift-fab'
import { ImpersonationBanner } from '@/components/dashboard/impersonation-banner'
import { SettingsModal } from '@/features/settings/settings-modal'
import type { OrganizationSlugLookup,Shop } from '@/types'

const SIDEBAR_COOKIE_NAME = 'sidebar_state'

export const Route = createFileRoute('/_dashboard')({
  beforeLoad: requireAuth(),
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  component: DashboardLayout,
})

function DashboardLayout() {
  const [activeStore, setActiveStore] = useState<Shop | null>(() => {
    return getSelectedStore()
  })
  
  const orgId = authManager.getOrgId()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const isFullBleed = pathname === '/terminal'

  // Shops come from the authenticated org-scoped list. The public
  // /organizations/:id endpoint deliberately returns branding fields only
  // (the shops relation carries license data and must not be readable
  // unauthenticated) — reading .shops from it broke on 2026-07-03.
  // Same key + fetch as useOrganizationShop and the other /shops consumers so
  // the cache entry is shared.
  const { data: shopsData } = useQuery({
    queryKey: queryKeys.shops('global'),
    queryFn: () => api.get<Shop[] | { data?: Shop[] }>('/shops'),
    enabled: !!orgId,
  })
  const { data: orgData } = useQuery({
    queryKey: queryKeys.organizationBranding(orgId || ''),
    queryFn: () => api.get<OrganizationSlugLookup>(`/organizations/${orgId}`),
    enabled: !!orgId,
  })

  const stores = useMemo(
    () => (Array.isArray(shopsData) ? shopsData : shopsData?.data ?? []),
    [shopsData],
  )
  const [isStoreModalOpen, setIsStoreModalOpen] = useState(false)

  const handleStoreSelect = useCallback((store: Shop) => {
    setSelectedStore(store)
    setActiveStore(store)
    setIsStoreModalOpen(false)
  }, [])

  useEffect(() => {
    const unsubscribe = subscribeSelectedStore(() => {
      // Keep the object identity stable when the selected store hasn't actually
      // changed. getSelectedStore() returns a freshly parsed object every call,
      // so without this guard every store event would hand AppSidebar a new
      // `activeStore` reference and re-render its Radix/motion subtrees.
      setActiveStore((prev) => {
        const next = getSelectedStore()
        if (prev?.id === next?.id) return prev
        return next
      })
    })

    return unsubscribe
  }, [])

  // The persisted selection can point at a store this user can no longer
  // access (shop-scoped staff — GET /shops is filtered server-side). Left
  // alone it would stick and every shop-keyed query would 403; snap to the
  // first permitted store instead.
  useEffect(() => {
    if (stores.length === 0 || !activeStore) return
    if (stores.some((s) => s.id === activeStore.id)) return
    const fallback = stores[0]
    if (!fallback) return
    let cancelled = false
    // Deferred like the auto-select effect below — handleStoreSelect writes
    // localStorage AND sets state, so keep it out of the synchronous effect body.
    queueMicrotask(() => {
      if (!cancelled) handleStoreSelect(fallback)
    })
    return () => {
      cancelled = true
    }
  }, [stores, activeStore, handleStoreSelect])

  useEffect(() => {
    if (!orgData || activeStore) return

    let cancelled = false

    if (stores.length === 1 && stores[0]) {
      const store = stores[0]
      queueMicrotask(() => {
        if (!cancelled) handleStoreSelect(store)
      })
    } else if (stores.length > 1) {
      queueMicrotask(() => {
        if (!cancelled) setIsStoreModalOpen(true)
      })
    }

    return () => {
      cancelled = true
    }
  }, [activeStore, handleStoreSelect, orgData, stores])

  // Sidebar state control
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [isSidebarInitialized, setIsSidebarInitialized] = useState(false)
  const prevSidebarOpenRef = useRef<boolean | null>(null)

  // Initialize sidebar state based on cookie
  useEffect(() => {
    const initSidebar = () => {
      const cookieValue = document.cookie
        .split('; ')
        .find(row => row.startsWith(`${SIDEBAR_COOKIE_NAME}=`))
        ?.split('=')[1]

      if (cookieValue !== undefined) {
        setSidebarOpen(cookieValue === 'true')
      } else {
        setSidebarOpen(window.innerWidth >= 1024)
      }
      setIsSidebarInitialized(true)
    }

    initSidebar()
  }, [])

  useEffect(() => {
    if (!isSidebarInitialized) return

    if (isFullBleed) {
      setSidebarOpen((current) => {
        prevSidebarOpenRef.current = current
        return false
      })
    } else if (prevSidebarOpenRef.current !== null) {
      const prev = prevSidebarOpenRef.current
      prevSidebarOpenRef.current = null
      setSidebarOpen(prev)
    }
  }, [isFullBleed, isSidebarInitialized])

  if (!isSidebarInitialized) {
    return null
  }

  const layout = (
    <SidebarProvider open={sidebarOpen} onOpenChange={setSidebarOpen} className={isFullBleed ? 'h-svh overflow-hidden' : undefined}>
      <ImpersonationBanner />
      <AppSidebar stores={stores} activeStore={activeStore} onSelectStore={handleStoreSelect} {...(isFullBleed ? { collapsible: 'offcanvas' as const } : {})} />
      {/* min-w-0: <main> is a flex item, so by default it can't shrink below
          its content's min-content width — and shadcn table cells are
          nowrap, so one wide table stretched the whole page sideways and
          pushed list side panels (and the header) off-screen. */}
      <SidebarInset className={isFullBleed ? 'min-w-0 overflow-hidden' : 'min-w-0'}>
        <Header
          onSwitchStore={() => setIsStoreModalOpen(true)}
          leftSlot={isFullBleed ? <PosHeaderLeft /> : undefined}
          centerSlot={isFullBleed ? <PosSearchInput /> : undefined}
          rightSlot={isFullBleed ? <PosHeaderRight /> : undefined}
          hideFeedback={isFullBleed}
          hideSidebarTrigger={isFullBleed}
          compact={isFullBleed}
        />
        {isFullBleed ? (
          <div className="flex-1 flex flex-col overflow-hidden min-h-0">
            <Outlet />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-6">
            <div className="max-w-7xl mx-auto space-y-6" key={activeStore?.id}>
              <Outlet />
            </div>
          </div>
        )}
      </SidebarInset>

      <StoreSelectionModal
        isOpen={isStoreModalOpen}
        stores={stores}
        organization={orgData}
        onSelect={handleStoreSelect}
      />

      {!isFullBleed && <ResumeShiftFab />}

      <SettingsModal />
    </SidebarProvider>
  )

  return (
    <PosTerminalUIProvider>
      <PosSessionProvider>{layout}</PosSessionProvider>
    </PosTerminalUIProvider>
  )
}
